import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ShuffleIcon } from 'lucide-react';
import { Logo } from '../components/Logo';
import { ThemeToggle } from '../components/ThemeToggle';
import { PasswordModal } from '../components/case-study/PasswordModal';
import { PhotoAlbum } from '../components/coffee-table/PhotoAlbum';
import { BookPile } from '../components/coffee-table/BookPile';
import { Cup, Flowers } from '../components/coffee-table/TableDecor';
import { books } from '../data/coffeeTable';
import { profile } from '../data/profile';
import { useCoffeeTableAccess } from '../hooks/useCoffeeTableAccess';
import { matchesCoffeeTablePassword } from '../utils/caseStudyLock';
export function CoffeeTable() {
  const navigate = useNavigate();
  const {
    isUnlocked,
    grantAccess
  } = useCoffeeTableAccess();
  // Back-to-front stacking order of the book pile; the last id is on top.
  const [order, setOrder] = useState(() => books.map((_, i) => i));
  const [openId, setOpenId] = useState(-1);
  const pick = (id: number) => {
    setOrder(o => [...o.filter(x => x !== id), id]);
    setOpenId(id);
  };
  const shuffle = () => {
    setOrder(o => [...o.slice(1), o[0]]);
    setOpenId(-1);
  };
  return <div className="min-h-screen w-full bg-canvas text-fg" data-id="element-913" data-mp="36f32hwhvj1au">
      <header className="sticky top-0 z-40 bg-canvas/90 backdrop-blur" data-id="element-914" data-mp="3b8forzz2yw0f">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between px-5 py-4 md:px-8" data-id="element-915" data-mp="3g7im0y8tjxxu">
          <Link to={{
          pathname: '/',
          hash: '#playground'
        }} className="group inline-flex items-center gap-1.5 text-sm font-medium text-fg-muted transition-colors duration-150 hover:text-ink" data-id="element-916" data-mp="3l50wit2hil7y">
            <ArrowLeftIcon className="h-4 w-4 transition-transform duration-200 ease-out group-hover:-translate-x-0.5" data-id="element-917" data-mp="3u5ysco2rrmfg" />
            Playground
          </Link>
          <div className="flex items-center gap-3" data-id="element-918" data-mp="1zrxua5f1n3gf">
            <ThemeToggle data-id="element-919" data-mp="1zuk0a0madawj" />
            <Link to="/" className="flex items-center gap-2" data-id="element-920" data-mp="333d81iy44fsj">
              <Logo className="h-8 w-8" data-id="element-921" data-mp="333n3hm1xff5m" />
              <span className="hidden text-sm font-semibold text-fg sm:inline" data-id="element-922" data-mp="2y4jx9ya7n2w2">{profile.name}</span>
            </Link>
          </div>
        </div>
      </header>

      {isUnlocked ? <main className="mx-auto flex max-w-[1200px] flex-col gap-8 px-4 pb-16 pt-8 md:px-8 md:pt-12" data-id="element-923" data-mp="382hjkg8iepm1">
          <section className="flex flex-wrap items-end justify-between gap-6" data-id="element-924" data-mp="3m293sypmd28h">
            <div className="flex max-w-[640px] flex-col gap-3" data-id="element-925" data-mp="3m0639kqubqpi">
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-rust" data-id="element-926" data-mp="3h2cymw56r4rc">Pull up a chair</p>
              <h1 className="text-[40px] font-semibold leading-[1.08] tracking-[-0.02em] text-ink md:text-[56px]" data-id="element-927" data-mp="3vrixgj042syp">
                On my coffee table
              </h1>
              <p className="max-w-2xl text-[15px] leading-relaxed text-fg-muted" data-id="element-928" data-mp="8mkxkjn9177y">
                Drag a page or tap its corner to flip the album. Tap a book to open it.
              </p>
            </div>
            <button type="button" onClick={shuffle} className="inline-flex h-12 items-center gap-2 rounded-full bg-ink pl-[22px] pr-[26px] text-[15px] font-semibold text-white shadow-[0_4px_14px_rgba(30,75,72,0.22)] transition-[background-color,transform] duration-150 ease-out hover:bg-ink-deep active:scale-[0.97]" data-id="element-929" data-mp="n9rwfqzgmeqh">
              <ShuffleIcon className="h-4 w-4" aria-hidden data-id="element-930" data-mp="4jtt1o6lx9t6" />
              Shuffle the pile
            </button>
          </section>

          <section aria-label="Coffee table" className="relative rounded-3xl bg-[#DAEFE8] px-3.5 pb-7 pt-5 shadow-[inset_0_0_0_10px_#C9E5DB] md:rounded-[32px] md:px-10 md:pb-12 md:pt-7" data-id="element-931" data-mp="9bbro5l5qyaz">
            <div className="relative flex h-[84px] items-center justify-between md:h-[120px]" data-id="element-932" data-mp="3vq4n30d79ab0">
              <Flowers data-id="element-933" data-mp="3hqlrf71krrfl" />
              <Cup data-id="element-934" data-mp="3hsg27sj4u35y" />
            </div>
            <div className="grid items-start gap-7 lg:grid-cols-[minmax(0,1fr)_420px] lg:gap-10" data-id="element-935" data-mp="3mpg7i185r1w0">
              <PhotoAlbum data-id="element-936" data-mp="3eoqovw3hmi28" />
              <div className="flex flex-col items-center gap-[18px]" data-id="element-937" data-mp="3enz4zij968rs">
                <BookPile order={order} openId={openId} onPick={pick} onClose={() => setOpenId(-1)} data-id="element-938" data-mp="39p4b9ljqyzsw" />
              </div>
            </div>
          </section>

          <footer className="pt-2 text-xs text-fg-faint" data-id="element-939" data-mp="34wb6wys6xthc">
            Designed with love <span aria-label="love" role="img" data-id="element-940" data-mp="30wu0o34xwsb4">♥️</span> by Kat Diaz - 2026
          </footer>
        </main> : <>
          <main aria-hidden className="pointer-events-none mx-auto max-w-[1200px] select-none px-5 pb-16 pt-8 blur-md md:px-8 md:pt-12" data-id="element-941" data-mp="2qywfjoidnbmq">
            <div className="h-3 w-32 rounded-full bg-coral-soft" data-id="element-942" data-mp="2m038adtoogv0" />
            <p className="mt-4 text-[40px] font-semibold leading-[1.08] tracking-tight text-ink md:text-[56px]" data-id="element-943" data-mp="2hz9mpr3pre5a">
              On my coffee table
            </p>
            <div className="mt-8 h-[520px] rounded-[32px] bg-[#DAEFE8]" data-id="element-944" data-mp="g6v5ku8zudpx" />
          </main>
          <PasswordModal open verify={matchesCoffeeTablePassword} onSuccess={grantAccess} onClose={() => navigate({
        pathname: '/',
        hash: '#playground'
      })} description="This one's personal: family photos and the books on my table. Pop in the secret word and pull up a chair." askSubject="Coffee table password" data-id="element-945" data-mp="1y4hseh8xc5gc" />
        </>}
    </div>;
}